import React from 'react';
import { FieldValues, UseFormSetError } from 'react-hook-form';
import { DynamicForm } from '@/components/forms/dynamic-form/DynamicForm';
import { FormHeader } from '@/components/forms/form-header/FormHeader';
import styles from './LoginForm.module.scss';
import { createSchema, IAPIFormSchema } from '@/services/formSchema.service';
import { useTranslation } from '@app/i18n/useTranslationClient';
import { useLogin } from '@/hooks/api/useLogin/useLogin';
import { onLogin } from '@packages/authenticator';
import { handleFormServerErrors } from '@/components/dynamic-field/field-input/fieldInput.helper';
import { ILoginRequest } from '@/hooks/api/useLogin/useLogin.interfaces';
import { ILoginFormSchemaResponse } from '@/hooks/api/fetchLoginFormSchema/useLoginFormSchema.interfaces';

interface ILoginForm {
    shouldDisplayHeader: boolean;
    schema: ILoginFormSchemaResponse;
    onNextStep?(): void;
    onLogin(): void;
}

export const LoginForm = (props: ILoginForm) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { login, loading } = useLogin();

    const loginSchema = createSchema(props.schema as unknown as IAPIFormSchema, language, t);

    const onFormSubmit = async (data: FieldValues, setErrors: UseFormSetError<any>) => {
        const response = await login(data as ILoginRequest);

        if (response.success) {
            onLogin({
                token: response.token,
                onLogin: props.onLogin,
            });
        }

        if (response.errors) {
            handleFormServerErrors(setErrors, response.errors);
        }
    };

    return (
        <div data-testid={'loginForm'}>
            {props.shouldDisplayHeader && <FormHeader label={t('translations.login.form.title')} />}
            <div className={styles.form}>
                <DynamicForm
                    formSchema={loginSchema}
                    formSubmitButtonLabel={t('translations.login.form.loginButton')}
                    onFormSubmit={onFormSubmit}
                    isSubmitButtonDisabled={loading}
                    element={
                        <div className={styles.formElement}>
                            <span
                                data-testid={'nextStepButton'}
                                onClick={props.onNextStep}
                                className={styles.formElementLink}>
                                {t('translations.login.form.password.forgot')}
                            </span>
                        </div>
                    }
                />
            </div>
        </div>
    );
};
